/**
 * Toast.tsx — varselboks fast posisjonert på VENSTRE side av skjermen
 *
 * Enkel, selvstendig varselboks i light DOM (createPortal til document.body).
 * Brukes der InlineNotification ikke passer (f.eks. bekreftelse etter lagret
 * chat-melding i Carbon AI Chat — widgeten er shadow DOM, så en boks inne i
 * den er ikke praktisk; denne ligger helt utenfor).
 *
 * Hvordan det fungerer:
 * 1. Rendres via createPortal til document.body — fixed posisjon venstre
 * 2. Gjenbruker Carbon low-contrast notification-farger (samme tokens som
 *    InlineNotification)
 * 3. Auto-lukk håndteres av kalleren (useToast-hook) — komponenten er dumb
 * 4. Lyd: Success.mp3 ved kind=success, Error.mp3 ved kind=error
 *    (fra /public/Sound — kan dempes via playSound: false)
 *
 * Bruk:
 *   const { toast, showToast } = useToast();
 *   showToast({ kind: 'success', title: 'Melding sendt' });
 *   {toast}
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckmarkFilled, Close } from '@carbon/icons-react';
import styles from './toast.module.css';

type ToastKind = 'error' | 'success' | 'info' | 'warning';

// Lydfiler fra /public/Sound — spilles av ved visning av varselboksen.
// info/warning har ingen lydfil (tom streng = ingen avspilling).
const SOUND_BY_KIND: Record<ToastKind, string> = {
  success: '/Sound/Success.mp3',
  error: '/Sound/Error.mp3',
  info: '',
  warning: '',
};

interface ToastOptions {
  kind: ToastKind;
  title: string;
  /** Valgfri undertekst */
  subtitle?: string;
  /** Auto-lukk etter N ms (0 = aldri) — standard 3000 */
  autoCloseMs?: number;
  /** Spill av lyd ved visning (default true for success/error) */
  playSound?: boolean;
}

/**
 * Hook: holder toast-state og returnerer portal-noden.
 *
 * 1. showToast() — viser varselboksen (valgfri auto-lukk)
 * 2. toast — ReactNode (portal til document.body) som kalleren rendrer
 */
export function useToast() {
  const [options, setOptions] = useState<ToastOptions | null>(null);
  const timerRef = useRef<number | null>(null);

  // Rydd opp timer ved unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const showToast = useCallback((opts: ToastOptions) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    const autoCloseMs = opts.autoCloseMs ?? 3000;
    if (autoCloseMs > 0) {
      timerRef.current = window.setTimeout(() => {
        setOptions(null);
        timerRef.current = null;
      }, autoCloseMs);
    }
    setOptions(opts);

    // Lyd: Success.mp3 / Error.mp3 fra /public/Sound. Ny Audio-instans per
    // avspilling slik at gjentatte send på rad ikke kolliderer. playSound
    // kan settes til false for å dempe (f.eks. i tester).
    const playSound = opts.playSound ?? true;
    const soundSrc = SOUND_BY_KIND[opts.kind];
    if (playSound && soundSrc) {
      try {
        const audio = new Audio(soundSrc);
        void audio.play().catch(() => {
          // Autoplay-policy: nettleseren kan blokkere lyd uten bruker-
          // interaksjon. Meldingen sendes alltid etter klikk på send, så
          // dette bør normalt aldri slå inn — men vi feiler stille.
        });
      } catch {
        // Audio-konstruksjon kan feile i ikke-nettleser-miljøer — stille.
      }
    }
  }, []);

  const hideToast = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOptions(null);
  }, []);

  // Portal til document.body — light DOM, helt utenfor shadow DOM
  const toast =
    options !== null
      ? createPortal(
          <div
            className={`${styles.toast} ${styles[options.kind]}`}
            role="status"
            aria-live="polite"
          >
            {options.kind === 'success' && (
              <CheckmarkFilled size={16} className={styles.icon} />
            )}
            <div className={styles.content}>
              <p className={styles.title}>{options.title}</p>
              {options.subtitle && (
                <p className={styles.subtitle}>{options.subtitle}</p>
              )}
            </div>
            <button
              type="button"
              className={styles.close}
              onClick={hideToast}
              aria-label="Lukk varsel"
            >
              <Close size={16} />
            </button>
          </div>,
          document.body
        )
      : null;

  return { toast, showToast, hideToast };
}